import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  FileText,
  Home,
  Search,
  Settings,
  Upload,
  User,
  Users,
} from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { AccountType } from '@/__generated__/graphql';
import { setUserContext } from '@/actions/ui';
import config from '@/config';
import { QUEUE_IDS } from '@/core/apiWebSocket';
import { UserContext } from '@/core/user';
import { canViewEngagementSurfaces } from '@/features/consulting-engagements/gate';
import NotificationBellLive from '@/features/consulting-engagements/live/NotificationBellLive';
import { useIsGovernanceApprover } from '@/features/consulting-engagements/useGovernanceApprover';
import { useApp } from '@/hooks/useAppContext';
import { updateUnreadCount } from '@/messaging/store';
import { fetchConflictsCount } from '@/profile/store';
import { RootState } from '@/store';

import Button from '../Button';
import Header, { NavBarItem } from '../Header';
import SelectGroup from '../SelectGroup';
import TeamAccountPromoButton from '../TeamAccountPromo/TeamAccountPromoButton';
// ENG-5251 (PR #2688 review S1): nav-menu Find Experts now shares the
// desktop button's 3-branch logic via useFindExpertsAction. Previously
// the mobile path was 2-branch (SMB → modal | else → navigate
// /request_expert), so non-SMB no-groups viewers got a broken page on
// mobile vs. Contact Sales on desktop.
import { useFindExpertsAction } from '../TeamAccountPromo/useFindExpertsAction';
import { BadgeCount } from './UserMenu';
import { navItemsBuilder } from './navItemsBuilder';

interface UserContextOptionProps {
  option: {
    name: string;
    description?: string;
  };
}

const UserContextOption = ({ option }: UserContextOptionProps) => {
  return (
    <div className="flex items-center">
      {option.name}
      {option.description && <span className="ml-4">({option.description})</span>}
    </div>
  );
};

const connector = connect(
  (state: RootState) => ({
    viewer: state.viewer,
    userContext: state.ui.userContext,
    userContextOptions: state.ui.userContextOptions,
    unreadCount: state.messaging.unreadCount,
    conflictsCount: state.profiles.counts.conflicts,
  }),
  {
    updateUnreadCount,
    setUserContext,
    fetchConflictsCount,
  }
);

interface AccessHeaderProps {
  SearchComponent?: React.ComponentType;
  shouldShowNewRequest: boolean;
}

const AccessHeader = ({
  viewer,
  unreadCount,
  userContext,
  userContextOptions,
  conflictsCount,
  SearchComponent,
  updateUnreadCount,
  setUserContext,
  fetchConflictsCount,
  shouldShowNewRequest,
}: AccessHeaderProps & ConnectedProps<typeof connector>): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const { apiWebSocket } = useApp();

  const [navItems, setNavItems] = useState<NavBarItem[]>([]);
  // ENG-5251 (PR #2688 review S1): share the 3-branch Find Experts logic
  // with the desktop button. The hook owns the SMB-signup modal and the
  // Contact Sales dialog state; we just dispatch from the nav-menu click.
  const findExperts = useFindExpertsAction({ to: '/request_expert' });
  const handleFindExpertsClick = useCallback(() => {
    if (findExperts.branch === 'smb-signup') {
      findExperts.openSignup();
      return;
    }
    if (findExperts.branch === 'contact-sales') {
      findExperts.openContactSales();
      return;
    }
    if (findExperts.to) {
      navigate(findExperts.to);
    }
  }, [findExperts, navigate]);

  const isAccountEnterprise = viewer.groups?.find((g) => g.account_type === AccountType.Enterprise);

  // Consulting engagements (ENG-5399) nav entries.
  //
  // The Engagements entry is derived from props alone, with no query.
  //
  // Approvals is NOT, as of ENG-5654. It used to be, on the reasoning that an
  // empty approver inbox is a legitimate state and the page says "All caught
  // up" — but the queue is approver-only and governance resolves to the team's
  // active owner, a set of one, so that handed a permanently empty entry to
  // everyone else on the team. What made the old trade-off look cheap was
  // costing `myGovernanceQueue`, an unbounded list of hydrated engagements, on
  // every page load. A boolean the api answers with one EXISTS is not that, so
  // the trade came out the other way.
  //
  // The notification bell (ENG-5451) queries too: a server-side COUNT for its
  // badge and nothing else, fetching the feed only when the panel is opened.
  // Counting unread rows client-side would mean pulling the whole notification
  // list on every page load — strictly worse than the one small query.
  //
  // So this component needs an ApolloProvider, and tests that render the chrome
  // either supply one or mock the hook (AccessHeader.test.tsx does the latter).
  const engagementsEnabled = canViewEngagementSurfaces(viewer, userContext as UserContext);
  // Gates the Approvals nav item alone (ENG-5654). Skipped, and so free, for
  // every viewer the nav would not offer it to anyway: the feature has to be on
  // AND the viewer has to be customer-side, since an expert never sees the item
  // whatever the answer would have been.
  const isGovernanceApprover = useIsGovernanceApprover(
    engagementsEnabled && userContext !== 'expert'
  );

  const handleChangeContext = useCallback(
    (newContext: string) => {
      setUserContext(newContext as UserContext);
      if (newContext === 'expert' && location.pathname === '/expert_requests') {
        navigate('/dashboard');
      } else if (location.pathname.startsWith('/settings/team/')) {
        // Switching team context while on team settings — update slug in URL
        const targetGroup = viewer.groups?.find(
          (g: { id: string; slug?: string | null }) => g.id === newContext
        );
        if (targetGroup?.slug) {
          // Replace old team slug with new one, preserve the section path
          const afterTeam = location.pathname.replace(/^\/settings\/team\/[^/]+/, '');
          navigate(`/settings/team/${targetGroup.slug}${afterTeam}`);
        } else {
          navigate('/settings');
        }
      } else if (location.pathname.startsWith('/team/')) {
        const targetGroup = viewer.groups?.find(
          (g: { id: string; slug?: string | null }) => g.id === newContext
        );
        if (targetGroup?.slug) {
          navigate(`/team/${targetGroup.slug}`);
        } else {
          navigate('/team/default');
        }
      } else {
        navigate(location);
      }
    },
    [location, navigate, setUserContext, viewer.groups]
  );

  const handleGroupContext = useCallback(
    (value: string) => {
      if (!value) return;
      handleChangeContext(value);
    },
    [handleChangeContext]
  );

  const userMenuItems: NavBarItem[] = [];

  // build "View As" collapsible with all context options
  const currentOption = userContextOptions.find((o) => o.value === userContext);
  const currentLabel = currentOption?.name || viewer.first_name;

  const contextChildren: NavBarItem[] = userContextOptions.map((option) => ({
    key: `${option.value}-context`,
    text: (
      <span className={option.value === userContext ? 'font-medium text-brand-primary' : ''}>
        <UserContextOption option={option} />
      </span>
    ),
    onClick: () => {
      handleChangeContext(option.value);
    },
  }));

  userMenuItems.push({
    key: 'view-as',
    text: (
      <span className="min-w-0">
        <span className="mb-2 block text-[12px] text-on-surface-muted">View as</span>
        <span className="block truncate font-medium text-on-surface">{currentLabel}</span>
      </span>
    ),
    children: contextChildren,
    divider: true,
  });

  if (viewer.admin) {
    userMenuItems.push({
      key: 'context-selector',
      text: (
        <SelectGroup
          autoComplete
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={handleGroupContext}
          className="m-0 p-0 w-full"
          margin="none"
        />
      ),
      handleKeyDown: (e) => {
        e.stopPropagation();
      },
      divider: true,
    });
  }

  // build normal menu items
  userMenuItems.push(
    ...[
      {
        key: 'dashboard',
        text: 'Dashboard',
        icon: Home,
        active: location.pathname === '/dashboard',
        onClick: () => {
          navigate('/dashboard');
        },
      },
      {
        key: 'profile',
        text: 'My Profile',
        icon: User,
        active: location.pathname.startsWith('/profile/'),
        onClick: () => {
          navigate(`/profile/${viewer.username}`);
        },
      },
      ...(viewer.admin
        ? [
            {
              key: 'teams',
              text: 'Teams',
              icon: Users,
              active: location.pathname === '/teams',
              onClick: () => {
                navigate('/teams');
              },
            },
          ]
        : []),
      {
        key: 'settings',
        text: 'Settings',
        icon: Settings,
        active: location.pathname.startsWith('/settings'),
        onClick: () => {
          navigate(`/settings`);
        },
        divider: true,
      },
    ]
  );

  // build admin menu items
  if (viewer.admin) {
    userMenuItems.push(
      ...[
        {
          key: 'applications',
          text: 'View Applications',
          icon: FileText,
          to: '/search?expert_states[]=applied&expert_states[]=applying&sort=created.at&asc=false',
        },
        {
          key: 'profile-uploader',
          text: 'Profile Uploader',
          icon: Upload,
          onClick: () => {
            navigate(`/profile-uploader`);
          },
        },
        {
          key: 'profile-conflicts',
          icon: AlertTriangle,
          text: (
            <div className="flex items-center">
              Profile Conflicts <BadgeCount count={conflictsCount} />
            </div>
          ),
          onClick: () => {
            navigate(`/profile-conflicts`);
          },
          divider: true,
        },
      ]
    );
  }

  if (shouldShowNewRequest) {
    userMenuItems.push({
      key: 'expert-requests',
      icon: Search,
      text: <span className="text-brand-tertiary">Find Experts</span>,
      onClick: handleFindExpertsClick,
    });
  }

  // updateUnreadCount must come from props (the connected dispatch), not from the
  // module import of the same name — that import is a thunk creator, so calling it
  // built a thunk and discarded it, and the badge never updated (ENG-5528).
  const handleNewUnreadCount = useCallback(
    (payload: Record<string, any>) => {
      updateUnreadCount(payload.count);
    },
    [updateUnreadCount]
  );

  useEffect(() => {
    if (!viewer.id) {
      setNavItems([
        {
          key: 'login',
          text: 'Login',
          to: '/login',
        },
        {
          key: 'signup',
          text: 'Sign Up',
          to: '/signup?next=%2Fdashboard',
        },
      ]);
      return;
    }

    const navItems = navItemsBuilder({
      unreadMessages: unreadCount > 0 ? unreadCount : null,
      userContext: userContext,
      isAdmin: !!viewer.admin,
      isAccountEnterprise,
      hasExpertState: !!viewer.expert_state,
      engagementsEnabled,
      isGovernanceApprover,
    });
    setNavItems(
      navItems.map((item) => ({
        ...item,
        onClick: () => item.to && navigate(item.to),
      }))
    );
  }, [
    viewer,
    userContext,
    unreadCount,
    isAccountEnterprise,
    shouldShowNewRequest,
    navigate,
    engagementsEnabled,
    isGovernanceApprover,
  ]);

  useEffect(() => {
    if (!viewer.id) return;
    if (!config.enableChat) return;

    unsubscribeRef.current = apiWebSocket.on(
      QUEUE_IDS.CHAT_UNREAD_MESSAGES,
      viewer.id,
      handleNewUnreadCount
    );

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [apiWebSocket, handleNewUnreadCount, viewer.id]);

  useEffect(() => {
    if (!viewer.id) return;
    if (viewer.admin) fetchConflictsCount();
  }, [fetchConflictsCount, viewer]);

  const displayName = useMemo(() => {
    const context = userContextOptions.find((o) => o.value === userContext);

    if (!context) return `${viewer.first_name} ${viewer.last_name}`;

    return context.name + (context.description ? ` (${context.description})` : '');
  }, [viewer, userContext, userContextOptions]);

  return (
    <>
      {findExperts.surface}
      <Header
        userData={{
          displayName,
          picture_url: viewer.picture_url as string,
          username: viewer.username || undefined,
        }}
        isAuthenticated={!!viewer.id}
        SearchComponent={SearchComponent}
        findExperts={
          shouldShowNewRequest ? (
            <div className="hidden xl:block">
              <TeamAccountPromoButton
                component={Button}
                to="/request_expert"
                size="small"
                label="Find Experts"
                style={{ minWidth: 115 }}
              />
            </div>
          ) : null
        }
        navbarItems={navItems}
        userMenuItems={userMenuItems}
        notificationBell={
          engagementsEnabled ? <NotificationBellLive viewerUserId={viewer.id ?? ''} /> : null
        }
        logout={() => navigate('/logout')}
      />
    </>
  );
};

export default connector(AccessHeader);
